import { create } from "zustand";
import type { PaperFilters, SortField, SortDirection } from "@/hooks/useFilteredPapers";

export type PaperIdScopeKind =
  | "field-debate-camp"
  | "field-debate-bridges"
  | "field-debate-tension";

interface PersistedFilterState {
  filters: PaperFilters;
  sortField?: SortField;
  sortDirection?: SortDirection;
  wsId?: string;
  paperIdScope?: string[];
  paperIdScopeLabel?: string;
  paperIdScopeKind?: PaperIdScopeKind;
}

interface FilterState {
  filters: Record<string, PersistedFilterState>;
  setFilters: (
    conceptId: string,
    filters: PaperFilters,
    sortField?: SortField,
    sortDirection?: SortDirection,
    wsId?: string,
  ) => void;
  /**
   * Apply a paper-ID scope (camp, bridges, or tension union). Mutually
   * exclusive with wsId and filter chips — both are cleared when scope
   * is set so the banner UI is the single source of truth.
   */
  setPaperIdScope: (
    conceptId: string,
    paperIds: string[],
    label: string,
    kind: PaperIdScopeKind,
  ) => void;
  clearPaperIdScope: (conceptId: string) => void;
}

export const useFilterStore = create<FilterState>((set) => ({
  filters: {},
  setFilters: (conceptId, filters, sortField, sortDirection, wsId) =>
    set((state) => ({
      filters: {
        ...state.filters,
        [conceptId]: {
          ...state.filters[conceptId],
          filters,
          sortField,
          sortDirection,
          wsId,
        },
      },
    })),
  setPaperIdScope: (conceptId, paperIds, label, kind) =>
    set((state) => ({
      filters: {
        ...state.filters,
        [conceptId]: {
          ...(state.filters[conceptId] ?? {}),
          // Mutual exclusion: clear wsId + filter chips when scope is set
          filters: {} as PaperFilters,
          wsId: undefined,
          paperIdScope: paperIds,
          paperIdScopeLabel: label,
          paperIdScopeKind: kind,
        },
      },
    })),
  clearPaperIdScope: (conceptId) =>
    set((state) => {
      const slice = state.filters[conceptId];
      if (!slice) return state;
      const { paperIdScope: _ps, paperIdScopeLabel: _l, paperIdScopeKind: _k, ...rest } = slice;
      return { filters: { ...state.filters, [conceptId]: rest } };
    }),
}));
